import React, { useState } from 'react'
import classNames from 'classnames'
import { Dropdown } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { type CrossClientQueryResult, CrossClientQueryResultMatches } from '../../../../models/CrossClient'
import { CcqCards, canChangeMatch, type CcqCard } from './ccqResultView'
import { CcqStatusIcon } from './CcqStatusIcon'
import { useCcqWorkspace, type CcqChangeableMatch } from './useCcqWorkspace'
import { CCQ_CARD_LABEL, CCQ_CHIP_LOCKED } from './ccqRunCopy'
import styles from './CcqRail.module.scss'
import { CCQ_SECONDARY_BUTTON_CLASS } from './ccqClassNames'

const CHIP_CLASS = classNames(CCQ_SECONDARY_BUTTON_CLASS, 'rounded-3 d-flex align-items-center gap-1 px-2 py-1 zocks-bg-color-white')

const CHIP_OPTIONS: { match: CcqChangeableMatch; card: CcqCard }[] = [
  { match: CrossClientQueryResultMatches.MATCH, card: CcqCards.MATCHED },
  { match: CrossClientQueryResultMatches.NO_MATCH, card: CcqCards.NOT_MATCHED },
]

interface CcqChipToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  card: CcqCard
  isHighlighted: boolean
  highlightKey: string
}

const CcqChipToggle = React.forwardRef<HTMLButtonElement, CcqChipToggleProps>(({ card, isHighlighted, highlightKey, className, ...rest }, ref) => (
  <Button ref={ref} type='button' variant='header' className={classNames(CHIP_CLASS, 'position-relative', className)} {...rest}>
    {/* Keyed so the highlight replays without remounting the button, which would drop its focus. */}
    {isHighlighted ? <span key={highlightKey} className={styles.chipChanged} aria-hidden='true' /> : null}
    <CcqStatusIcon card={card} size='small' />
    {CCQ_CARD_LABEL[card]}
    <i className={classNames('bi bi-chevron-expand', styles.tagIcon)} aria-hidden='true' />
  </Button>
))
CcqChipToggle.displayName = 'CcqChipToggle'

interface CcqStatusChipProps {
  result: CrossClientQueryResult
  card: CcqCard
  isHighlighted: boolean
  highlightKey: string
}

export const CcqStatusChip: React.FC<CcqStatusChipProps> = ({ result, card, isHighlighted, highlightKey }) => {
  const { moveClient } = useCcqWorkspace()
  const [isChanging, setIsChanging] = useState(false)
  const isChangeable = canChangeMatch(result)

  const handleSelect = async (match: CcqChangeableMatch) => {
    if (isChanging || result.match === match) {
      return
    }
    setIsChanging(true)
    await moveClient(result, match)
    setIsChanging(false)
  }

  return (
    <Tooltip title={isChangeable ? undefined : CCQ_CHIP_LOCKED} placement='top'>
      <Dropdown>
        <Dropdown.Toggle
          as={CcqChipToggle}
          bsPrefix='ccq-status-chip'
          card={card}
          isHighlighted={isHighlighted}
          highlightKey={highlightKey}
          disabled={!isChangeable}
          aria-busy={isChanging}
        />
        <Dropdown.Menu className='p-1'>
          {CHIP_OPTIONS.map((option) => (
            <Dropdown.Item
              key={option.match}
              as='button'
              disabled={result.match === option.match}
              onClick={() => void handleSelect(option.match)}
              className='d-flex align-items-center gap-2 paragraph-medium rounded-2 px-2 py-2'
            >
              <CcqStatusIcon card={option.card} size='small' />
              {CCQ_CARD_LABEL[option.card]}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </Tooltip>
  )
}
